import { useEffect, useState } from 'react';

function parseParams(hash: string): Record<string, string> {
  return hash
    .split('&')
    .slice(1)
    .reduce<Record<string, string>>((acc, segment) => {
      const [key, value] = segment.split('=');
      if (key) {
        acc[key] = value ?? '';
      }
      return acc;
    }, {});
}

function buildHref(baseHash: string, params: Record<string, string>): string {
  const queryString = Object.entries(params)
    .map(([k, v]) => `${k}=${v}`)
    .join('&');
  return `${baseHash}&${queryString}`;
}

function useNavParams() {
  'use memo';

  const [hash, setHash] = useState(() => location.hash);

  useEffect(() => {
    const onHashChange = () => setHash(location.hash);
    window.addEventListener('hashchange', onHashChange);
    return () => window.removeEventListener('hashchange', onHashChange);
  }, []);

  const baseHash = hash.split('&')[0];
  const params = hash ? parseParams(hash) : null;

  const setParams = (
    newParams: Record<string, string>,
    blockRefresh?: boolean
  ) => {
    const merged = { ...(params ?? {}), ...newParams };
    const href = buildHref(baseHash, merged);
    if (blockRefresh) {
      window.history.replaceState(null, document.title, href);
    } else {
      location.href = href;
    }
  };

  const removeParam = (key: string) => {
    const updated = { ...(params ?? {}) };
    delete updated[key];
    location.href = buildHref(baseHash, updated);
  };

  return { params, setParams, removeParam };
}

export default useNavParams;
